import { ClientNameType, TInvalidUpcCorrection, TMutationHandler } from "../types";

interface TNodeOptions {
  trimChars?: string;
  caseSensitive?: boolean;
  rootElement?: HTMLElement | Document;
}

// text input question: type0
// radio question (yes/no): qtype2
// radio question (ie. not a binary yes/no): qtype5
// textarea question: qtype7
// `select` dropdown question: qtype10
// upload photo container element classname: qtype11

export class MutationObserverCustom {
  private handlers: TMutationHandler[];
  private observerOptions: MutationObserverInit;
  private observer: MutationObserver;

  constructor(
    targetElement: HTMLElement,
    { attributes = true, subtree = true, ...options }: MutationObserverInit = {}
  ) {
    this.handlers = [];
    this.observerOptions = { attributes, subtree, ...options };

    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.target instanceof HTMLElement) {
          for (const handler of this.handlers) {
            handler(mutation);
          }
        }
      }
    });

    this.observer.observe(targetElement, this.observerOptions);
  }

  public addHandler(handler: TMutationHandler) {
    this.handlers.push(handler);
  }

  public getHandlers(): TMutationHandler[] {
    return this.handlers;
  }
}

export const QUESTION_ANSWER_SLEEP_TIME_MS = 15;

export const SHOW_MODAL_EVENT = new Event("showModal", { bubbles: true });

export const ALWAYS_IN_STOCK_NAME_RES = [/scott.*1000/i];

const UPC_FIRST_DIGITS: Record<ClientNameType, string> = {
  KC: "0",
  GM: "0",
  CLRX: "0",
  LAVAZZA: "0",
  BLUEBUFFALO: "0",
  YASSO: "8",
  JJMARTIN: "8",
  COS: "0",
  CHALLENGEDAIRY: "0",
  J_J: "3",
  FERR: "0",
  SANOFI: "0",
  KENVUE: "0",
  FAGE: "6",
  NEWMANS: "0",
  // FRENCH: ["0", "8"],
};

export const INCORRECT_UPCS: Record<ClientNameType, TInvalidUpcCorrection> = {
  CLRX: [
    { badUpc: "446003188", actualUpc: "044600318882" },
    { badUpc: "602585068", actualUpc: "060258506884" },
    { badUpc: "412960146", actualUpc: "041294601467" },
    { badUpc: "412960149", actualUpc: "041294601498" },
    { badUpc: "412960147", actualUpc: "041294601474" },
    { badUpc: "412960153", actualUpc: "041294601535" },
    { badUpc: "412960148", actualUpc: "041294601481" },
    { badUpc: "412960154", actualUpc: "041294601542" },
    { badUpc: "412960167", actualUpc: "041294419048" },
    { badUpc: "10044600600373", actualUpc: "044600600376" },
    { badUpc: "10044600309221", actualUpc: "044600036724" },
    { badUpc: "10044600300372", actualUpc: "044600300375" },
    { badUpc: "10044600300488", actualUpc: "044600300481" },
    { badUpc: "10044600309245", actualUpc: "044600009384" },
    { badUpc: "10012587223724", actualUpc: "012587223727" },
    { badUpc: "10012587791643", actualUpc: "012587791646" },
    { badUpc: "10071100009740", actualUpc: "071100009743" },
    { badUpc: "10071100480099", actualUpc: "071100480092" },
    { badUpc: "412960157", actualUpc: "041294601573" },
    { badUpc: "412960159", actualUpc: "041294601597" },
    { badUpc: "412960158", actualUpc: "041294417730" },
    { badUpc: "412960170", actualUpc: "041294972826" },
  ],
  GM: [{ badUpc: "160001052800512", actualUpc: "016000105287" }],
  KC: [{ badUpc: "540001006", actualUpc: "054000100604" }],
  SANOFI: [
    { badUpc: "068142102002", actualUpc: "681421020022" },
    { badUpc: "068142102653", actualUpc: "681421026536" },
    { badUpc: "068142102003", actualUpc: "681421020039" },
    { badUpc: "068142103612", actualUpc: "681421036122" },
    { badUpc: "068142103614", actualUpc: "681421036146" },
  ],
  BLUEBUFFALO: [],
  CHALLENGEDAIRY: [],
  COS: [],
  FERR: [],
  J_J: [],
  JJMARTIN: [],
  LAVAZZA: [],
  YASSO: [],
  KENVUE: [],
  FAGE: [],
  NEWMANS: [],
};

export function getFullUpc(surveySkuNumber: string, surveyClientName: ClientNameType): string {
  if (surveyClientName === "CLRX" && surveySkuNumber.length === 14) {
    const slicedSku = surveySkuNumber.slice(2, -1);
    return getFullUpc(slicedSku, surveyClientName);
  }

  for (const [clientName, upcDatalist] of Object.entries(INCORRECT_UPCS)) {
    for (const upcData of upcDatalist) {
      if (surveyClientName === clientName && upcData.badUpc === surveySkuNumber) {
        return upcData.actualUpc;
      }
    }
  }

  const expectedDigit1 = UPC_FIRST_DIGITS[surveyClientName];

  switch (surveySkuNumber.length) {
    case 12: {
      return surveySkuNumber;
    }
    // missing either category number (first digit) or check digit
    case 11: {
      return getFullUpcFromLength11(expectedDigit1, surveySkuNumber);
    }
    // missing category number (first digit) and missing check digit
    case 10: {
      const tempUpc = expectedDigit1 + surveySkuNumber;
      return tempUpc + getCheckDigit(tempUpc);
    }
    // No default
  }

  return "";
}

function getFullUpcFromLength11(expectedDigit1: string, truncatedUpc: string) {
  if (!truncatedUpc.startsWith(expectedDigit1) && isValidUpc(expectedDigit1 + truncatedUpc)) {
    return expectedDigit1 + truncatedUpc;
  }

  return truncatedUpc + getCheckDigit(truncatedUpc);
}

function getCheckDigit(testUpc: string) {
  const array = [...testUpc].reverse();

  let total = 0;
  array.forEach((digit: string, idx: number) => {
    total += idx % 2 === 0 ? Number.parseInt(digit) * 3 : Number.parseInt(digit);
  });

  const res = Math.ceil(total / 10) * 10 - total;

  // console.log(`Check digit is ${res}, type: ${typeof(res)}`);

  return res.toString();
}

function isValidUpc(testUpc: string) {
  return testUpc.length === 12 && testUpc.at(-1) === getCheckDigit(testUpc.slice(0, 11));
}

export function trimStr(str: string, ch: string) {
  let start = 0,
    end = str.length;

  while (start < end && str[start] === ch) {
    ++start;
  }

  while (end > start && str[end - 1] === ch) {
    --end;
  }

  return start > 0 || end < str.length ? str.slice(start, end) : str;
}

// export function actionOnLoad(callback: () => void) {
//   if (document.readyState === "complete") {
//     callback();
//   } else {
//     // window.addEventListener("load", callback, { once: true });
//     setTimeout(callback, 500);
//   }
// }

export async function hashCode(message: string) {
  const msgUint8 = new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", msgUint8); // hash the message
  const hashArray = [...new Uint8Array(hashBuffer)]; // convert buffer to byte array
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join(""); // convert bytes to hex string
  return hashHex;
}

export function rollOnPercent(targetPercent: number) {
  // rolls in range of [1, 100]
  const rolledNumber = randNum(1, 100);
  return rolledNumber <= targetPercent;
}

// returns a random number bewteen [min, max] inclusive
export function randNum(min: number, max: number) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function randChoice<T>(items: T[]): T {
  const index = Math.floor(Math.random() * items.length);
  return items[index];
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function getNormalizedStoreName(storeDetail: string): string {
  const storeNumberRegex = / *-* *\d* *$/i;

  // add space after commas, if none exist
  storeDetail = storeDetail.replaceAll(/\b(,) *\b/g, "$1 ");

  // regex for capturing the state at the end of the town/location.
  // State should be normalized to appear at the start of the substring
  const cityRegex = /(.*?), (ny|nj)( *\|.+)/i;
  const match = cityRegex.exec(storeDetail);

  // if location string is non-standard (eg. state appearing at end of town/location)
  if (match) {
    const city = match[2];
    const storeNameCityNormalized = storeDetail.replace(cityRegex, `${city}, $1$3`);
    return storeNameCityNormalized.replace(storeNumberRegex, "").toUpperCase();
  } else {
    return storeDetail.replace(storeNumberRegex, "").toUpperCase();
  }
}

export function getStoreMiddleAddress(storeName: string): string | null {
  const middleAddressRegex = /\| *([^|]+?) *\|/;
  const match = middleAddressRegex.exec(storeName);
  if (!match) {
    console.warn(`No match for middle address in store: "${storeName}"`);
    return null;
  }

  return match[1];
}

export function getGuid() {
  const urlParams = new URL(document.location.href).searchParams;
  const guid = urlParams.get("GUID") ?? urlParams.get("locationGuid");
  if (guid === null) {
    throw new Error("No GUID found in URL search params");
  }

  return guid;
}

export function nodeByText<T extends HTMLElement = HTMLElement>(
  selector: string,
  targetText: RegExp | string,
  { trimChars = " *", caseSensitive = true, rootElement = document }: TNodeOptions = {}
) {
  return nodesByText<T>(selector, targetText, { trimChars, caseSensitive, rootElement }).at(0) ?? null;
}

export function nodesByText<T extends HTMLElement = HTMLElement>(
  selector: string,
  targetText: RegExp | string,
  { trimChars = " *", caseSensitive = true, rootElement = document }: TNodeOptions = {}
): T[] {
  if (selector.trim() === "") {
    throw new Error("Selector is empty");
  }
  if (!(targetText instanceof RegExp) && targetText.trim() === "") {
    throw new Error("Target text is empty");
  }

  const result: T[] = [];

  const nodes = rootElement.querySelectorAll<T>(selector);
  if (nodes.length === 0) {
    console.warn(`No nodes found that match the selector "${selector}"`);
    return [...nodes];
  }

  for (const node of nodes) {
    let normalizedInnerText = getNormalizedText(node.innerText);
    // let nodeInnerText = node.innerText.replaceAll(/\s+/g, " ").trim();

    for (const char of trimChars) {
      normalizedInnerText = trimStr(normalizedInnerText, char);
    }
    normalizedInnerText = normalizedInnerText.trim();

    if (targetText instanceof RegExp) {
      const match = targetText.exec(normalizedInnerText);
      if (match) {
        result.push(node);
      }
    } else {
      // logByMatch(nodeInnerText, targetText, "system");

      if (caseSensitive && normalizedInnerText === targetText) {
        result.push(node);
      } else if (!caseSensitive && normalizedInnerText.toLowerCase() === targetText.toLowerCase()) {
        result.push(node);
      }
    }
  }

  if (result.length === 0) {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    console.warn(`No nodes found that match the innerText rule "${targetText}"`);
  }
  return result;
}

export function attemptElementAction<K extends keyof HTMLElement>(element: HTMLElement, method: K): void {
  const fn = element[method];
  if (typeof fn === "function") {
    // eslint-disable-next-line @typescript-eslint/ban-types
    (fn as Function).call(element);
  } else {
    throw new TypeError(`${method.toString()} is not a function on HTMLElement`);
  }
}

export async function attemptElementClick(
  maxAttempts: number,
  getElement: () => HTMLElement | undefined
): Promise<void> {
  for (let i = 0; i < maxAttempts; i++) {
    const el = getElement();
    if (!el) break;
    el.click();
    await sleep(300);
  }
}

export function isStatusCodeCategory(statusCode: number, category: "400" | "500") {
  return category === "400" ? 400 <= statusCode && statusCode < 500 : 500 <= statusCode && statusCode < 600;
}

export async function isIncognito() {
  try {
    // Tries to request persistent storage
    const isPersisted = await navigator.storage.persist();
    // If Chrome denies persist in Incognito
    return !isPersisted;
  } catch {
    return false; // Couldn't determine
  }
}

export function getNormalizedText(text: string): string {
  return text
    .replaceAll(/\r\n?/g, "\n") // normalize newlines
    .replaceAll(/ *\n{2,} */g, "\n") // collapse excess blank lines
    .split("\n")
    .map((line) => line.trim())
    .join("\n") // trim lines
    .replaceAll(/ {2,}/g, " ") // collapse inner spaces
    .replaceAll(/[\u00A0\u200B]/g, "") // remove zero-width/no-break space
    .normalize("NFC") // unicode normalization
    .trim();
}

/** Split text by newline char, making sure that no empty strings or surrounding whitespace ends up in the split strings */
export function getSplitCleanly(input: string): string[] {
  return input
    .replaceAll("\r\n", "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export function matchesText(el: HTMLElement, targetText: RegExp | string): el is HTMLElement {
  const text = el.innerText.trim();
  return typeof targetText === "string" ? text === targetText : targetText.test(text);
}

export async function waitForElementToExist(
  elementGetter: () => HTMLElement | null,
  timeoutSeconds = 25
): Promise<HTMLElement | null> {
  const timeoutMilliseconds = timeoutSeconds * 1000;
  const numberOfChecksBeforeWaitTimeIncrease = 15;
  let intervalWaitingTimeMs = 75;

  await sleep(intervalWaitingTimeMs);

  let currentNumberOfChecks = 0;
  let currentWaitTimeMs = intervalWaitingTimeMs;
  let targetElement: HTMLElement | null = null;

  while ((targetElement = elementGetter()) === null) {
    await sleep(intervalWaitingTimeMs);
    currentWaitTimeMs += intervalWaitingTimeMs;
    currentNumberOfChecks += 1;

    if (currentWaitTimeMs > timeoutMilliseconds) {
      return null;
    }

    if (currentNumberOfChecks > numberOfChecksBeforeWaitTimeIncrease) {
      intervalWaitingTimeMs = 300;
    }
  }

  return targetElement;
}

export async function waitForElementWithTextToExist(
  selector: string,
  targetText: RegExp | string,
  timeoutSeconds = 10
): Promise<HTMLElement | null> {
  const timeoutMs = timeoutSeconds * 1000;
  const startTime = Date.now();

  const findMatchingElement = (): HTMLElement | null => {
    const elements = [...document.querySelectorAll<HTMLElement>(selector)];
    return elements.find((el) => matchesText(el, targetText)) ?? null;
  };

  let element = findMatchingElement();

  while (element === null && Date.now() - startTime < timeoutMs) {
    await sleep(100);
    element = findMatchingElement();
  }

  return element;
}

function animateScroll(
  start: number,
  distance: number,
  duration: number,
  callback: (position: number) => void
): void {
  let startTime: number | null = null;

  function step(timestamp: number) {
    if (startTime === null) startTime = timestamp;
    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / duration, 1); // Ensure progress doesn't exceed 1
    const ease = progress < 0.5 ? 2 * progress ** 2 : 1 - Math.pow(-2 * progress + 2, 2) / 2; // Ease-in-out

    callback(start + distance * ease);

    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

/**
 * Scrolls to the specified element with an optional offset and speed.
 * @param el - The target element to scroll to.
 * @param offset - The offset in pixels to apply to the scroll position. Defaults to 200.
 * @param scrollSpeed - The speed multiplier for the scroll.
 *                      0 is instant, 1 is standard smooth scrolling, and values > 1 increase the speed.
 */
export function scrollToWithOffset(
  el: Element,
  { scrollSpeed = 1, offset = 200 }: { scrollSpeed?: number; offset?: number } = {}
): void {
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  const baseDurationMs = 500; // scrollSpeed = 1 -> smooth scroll -> 500ms

  if (scrollSpeed <= 0) {
    window.scrollTo(0, top);
  } else {
    const start = window.scrollY;
    const distance = top - start;
    const duration = baseDurationMs / scrollSpeed;

    animateScroll(start, distance, duration, (position) => {
      window.scrollTo(0, position);
    });
  }
}

export function isElementDataEntryButtonClick(element: HTMLElement) {
  return element.closest("a.list-btn.dataEnt") !== null;
}

export function handleDataEntryButtonClick(event: MouseEvent) {
  const elm = event.target as HTMLElement | null;
  if (elm === null || !isElementDataEntryButtonClick(elm)) {
    return;
  }

  scrollToWithOffset(elm);
}

export function isPlainObject(obj: unknown) {
  return typeof obj === "object" && obj !== null && Object.getPrototypeOf(obj) === Object.prototype;
}

export function isTextInput(inputElm: HTMLInputElement): boolean {
  const textInputTypes = [
    "text",
    "password",
    "email",
    "search",
    "tel",
    "url",
    "number",
    // "date",
    // "datetime-local",
    "month",
    "week",
    "time",
  ];
  return textInputTypes.includes(inputElm.type);
}

/**
 * Generates a random string of characters from the provided array.
 * The i-th string in the 'strings' array will become the i-th char in the result string, but randomly capitalized or lowercased.
 * Each string in the 'strings' array represents a union of possible characters, and one character will be randomly chosen from it.
 * @param strings - An array of characters to choose from.
 */
export function getRandomizedString(strings: string[]): string {
  return strings
    .map((str) => {
      const isUpperCase = Math.random() < 0.5;
      const selectedChar = randChoice([...str]);
      return isUpperCase ? selectedChar.toUpperCase() : selectedChar.toLowerCase();
    })
    .join("");
}
